import { useEffect, useState, useCallback, useRef } from "react";
import { API_BASE_URL } from "../utils/config";
import CustomSelect from "../components/CustomSelect";

const authHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const STATUS_LABEL = {
  PENDING:   "Menunggu",
  ACTIVE:    "Disetujui",
  RETURNING: "Menunggu Konfirmasi",
  RETURNED:  "Dikembalikan",
  REJECTED:  "Ditolak",
};

function statusBadge(status) {
  const map = {
    PENDING:   "badge-warning",
    ACTIVE:    "badge-info",
    RETURNING: "badge-warning",
    RETURNED:  "badge-success",
    REJECTED:  "badge-danger",
  };
  return (
    <span className={`badge ${map[status] || "badge-info"}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function ItemSearch({ items, value, onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = items.find((it) => String(it.id) === String(value));
  const filtered = query.trim()
    ? items.filter((it) => {
        const q = query.toLowerCase();
        return it.code.toLowerCase().includes(q) || it.name.toLowerCase().includes(q);
      })
    : items;

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={wrapRef} className="relative">
      <div className="glass-input flex items-center gap-2 cursor-text" onClick={() => setOpen(true)}>
        {selected && !open ? (
          <>
            <span className="flex-1 text-sm text-strong truncate">
              <span className="font-mono text-xs mr-1">{selected.code}</span>
              {selected.name}
            </span>
            <button type="button" onClick={(e) => { e.stopPropagation(); onChange(""); setQuery(""); setOpen(false); }}
              className="text-muted hover:text-strong leading-none">×</button>
          </>
        ) : (
          <input
            autoFocus={open}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={selected ? `${selected.code} - ${selected.name}` : "Ketik kode atau nama barang…"}
            className="flex-1 bg-transparent outline-none text-sm text-strong placeholder:text-muted min-w-0"
          />
        )}
      </div>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 glass-dropdown rounded-xl max-h-56 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-3 py-3 text-xs text-muted">Barang tidak ditemukan.</p>
          ) : (
            filtered.map((it) => (
              <button
                key={it.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(String(it.id)); setQuery(""); setOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-left text-sm transition-colors hover:bg-white/40 dark:hover:bg-white/5 ${String(it.id) === String(value) ? "bg-brand-500/10" : ""}`}
              >
                <span className="flex-1 min-w-0">
                  <span className="font-mono text-xs mr-1.5">{it.code}</span>
                  <span className="text-strong truncate">{it.name}</span>
                </span>
                <span className="text-xs text-muted ml-2 flex-shrink-0">stok: {it.quantity_available}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function MyLoanRequests() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [formItemId, setFormItemId] = useState("");
  const [formLocationId, setFormLocationId] = useState("");
  const [formQty, setFormQty] = useState("");
  const [formPicName, setFormPicName] = useState("");
  const [formDueDate, setFormDueDate] = useState("");
  const [formNote, setFormNote] = useState("");
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState("");
  // items queued for this same submission (multi-item loan), each a
  // self-contained package: { item_id, quantity, location_id, location_name, label, note }
  const [lineItems, setLineItems] = useState([]);

  // Return request modal
  const [returnReqModal, setReturnReqModal] = useState(null);
  const [returnReqPhoto, setReturnReqPhoto] = useState(null);
  const [returnReqPhotoPreview, setReturnReqPhotoPreview] = useState(null);
  const [returnReqNote, setReturnReqNote] = useState("");
  const [returnReqLoading, setReturnReqLoading] = useState(false);
  const [returnReqError, setReturnReqError] = useState("");
  const returnReqPhotoRef = useRef(null);

  const fetchLoans = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    fetch(`${API_BASE_URL}/api/loan-transactions/?${params}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => { setLoans(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { fetchLoans(); }, [fetchLoans]);

  useEffect(() => {
    if (!showForm) return;
    fetch(`${API_BASE_URL}/api/items/?page_size=500&status=active&temporary_only=false`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => setItems(data.results || []))
      .catch(() => {});
    fetch(`${API_BASE_URL}/api/locations/`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => setLocations(Array.isArray(data) ? data : data.results || []))
      .catch(() => {});
  }, [showForm]);

  const resetForm = () => {
    setFormItemId(""); setFormLocationId(""); setFormQty("");
    setFormPicName(""); setFormDueDate(""); setFormNote("");
    setFormError(""); setFormSuccess("");
    setLineItems([]);
  };

  // Snapshots the current entry form into a self-contained "package": its own
  // item, quantity, location, PIC, due date and note — none of it shared
  // with other queued packages.
  const buildLineFromCurrentEntry = (itemId, qtyNumber) => {
    const item = items.find((it) => it.id === itemId);
    const location = locations.find((l) => String(l.id) === String(formLocationId));
    return {
      item_id: itemId,
      quantity: qtyNumber,
      location_id: Number(formLocationId),
      location_name: location?.name || "-",
      label: item ? `${item.code} — ${item.name}` : `#${itemId}`,
      pic_name: formPicName.trim(),
      due_date: formDueDate,
      note: formNote.trim(),
    };
  };

  const validateCurrentEntry = () => {
    if (!formLocationId) return "Pilih gudang asal terlebih dahulu.";
    if (!formPicName.trim()) return "Nama PIC / Peminjam wajib diisi.";
    if (!formDueDate) return "Tanggal jatuh tempo wajib diisi.";
    return null;
  };

  // queue the currently-entered item as one more self-contained package
  // (own item, qty, location, PIC, due date, note) in this same submission
  const handleAddLineItem = () => {
    setFormError("");
    if (!formItemId) {
      setFormError("Pilih barang terlebih dahulu sebelum menambahkan ke daftar.");
      return;
    }
    const qtyNumber = Number(formQty);
    if (!qtyNumber || qtyNumber <= 0) {
      setFormError("Jumlah harus lebih dari 0.");
      return;
    }
    const entryError = validateCurrentEntry();
    if (entryError) {
      setFormError(entryError);
      return;
    }
    const itemId = Number(formItemId);
    if (lineItems.some((li) => li.item_id === itemId)) {
      setFormError("Barang sudah ada di daftar. Hapus dulu jika ingin mengubah qty.");
      return;
    }

    setLineItems((prev) => [...prev, buildLineFromCurrentEntry(itemId, qtyNumber)]);

    // clear EVERY field, including gudang/PIC/due date — nothing carries
    // over silently, each package must be filled in on its own so a value
    // left over from the previous item can never leak into this one
    setFormItemId("");
    setFormLocationId("");
    setFormQty("");
    setFormPicName("");
    setFormDueDate("");
    setFormNote("");
  };

  const handleRemoveLineItem = (itemId) => {
    setLineItems((prev) => prev.filter((li) => li.item_id !== itemId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(""); setFormSuccess("");

    // fold whatever is currently sitting in the entry fields into the list
    // of packages, alongside anything already queued via "+ Item" — each
    // package carries its own PIC/due date/note, not shared with the others.
    let allItems = [...lineItems];
    if (formItemId) {
      const qtyNumber = Number(formQty);
      if (!qtyNumber || qtyNumber <= 0) {
        setFormError("Jumlah harus lebih dari 0.");
        return;
      }
      const entryError = validateCurrentEntry();
      if (entryError) {
        setFormError(entryError);
        return;
      }
      const itemId = Number(formItemId);
      if (allItems.some((li) => li.item_id === itemId)) {
        setFormError("Barang sudah ada di daftar. Hapus salah satu sebelum submit.");
        return;
      }
      allItems.push(buildLineFromCurrentEntry(itemId, qtyNumber));
    }

    if (allItems.length === 0) {
      setFormError("Semua field wajib diisi.");
      return;
    }

    setFormLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/loan-transactions/`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          items: allItems.map((li) => ({
            item_id: li.item_id,
            location_id: li.location_id,
            quantity: li.quantity,
            pic_name: li.pic_name,
            due_date: li.due_date,
            note: li.note,
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data?.quantity?.[0] || data?.detail || JSON.stringify(data);
        throw new Error(msg);
      }
      setFormSuccess("Pengajuan berhasil dikirim! Menunggu persetujuan admin.");
      resetForm();
      fetchLoans();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleReturnReqPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReturnReqPhoto(file);
    setReturnReqPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmitReturnReq = async () => {
    if (!returnReqModal) return;
    if (!returnReqPhoto) {
      setReturnReqError("Foto pengembalian wajib diisi.");
      return;
    }
    setReturnReqLoading(true);
    setReturnReqError("");
    const fd = new FormData();
    fd.append("photo", returnReqPhoto);
    if (returnReqNote.trim()) fd.append("return_note", returnReqNote.trim());
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/loan-transactions/${returnReqModal.id}/request-return/`,
        { method: "POST", headers: { ...authHeaders() }, body: fd }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || data?.photo?.[0] || "Gagal mengajukan pengembalian.");
      setReturnReqModal(null);
      setReturnReqPhoto(null);
      setReturnReqPhotoPreview(null);
      setReturnReqNote("");
      fetchLoans();
    } catch (err) {
      setReturnReqError(err.message);
    } finally {
      setReturnReqLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-strong">Pengajuan Peminjaman</h1>
          <p className="text-sm text-muted mt-0.5">Ajukan peminjaman barang — admin akan memproses persetujuan</p>
        </div>
        <button
          type="button"
          onClick={() => { setShowForm((v) => !v); resetForm(); }}
          className="btn-primary"
        >
          {showForm ? "Batal" : "+ Ajukan Pinjam"}
        </button>
      </div>

      {/* Form Pengajuan */}
      {showForm && (
        <div className="glass-panel p-5 rounded-2xl">
          <h2 className="text-base font-semibold text-strong mb-4">Form Pengajuan Baru</h2>
          <p className="text-xs text-muted -mt-3 mb-4">
            Tiap kali klik <b>+ Item</b>, barang itu tersimpan sebagai bungkusan sendiri lalu semua field di bawah ini
            (termasuk Gudang, PIC, Jatuh Tempo, Catatan) dikosongkan lagi — isi ulang untuk barang berikutnya.
          </p>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Barang *</label>
              <ItemSearch items={items} value={formItemId} onChange={setFormItemId} />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Gudang Asal *</label>
              <CustomSelect
                value={formLocationId}
                onChange={setFormLocationId}
                placeholder="Pilih gudang..."
                options={locations.map((loc) => ({ value: String(loc.id), label: loc.name }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Jumlah *</label>
              <div className="flex gap-2">
                <input type="number" min="1" value={formQty} onChange={(e) => setFormQty(e.target.value)} className="glass-input" placeholder="e.g. 2" />
                <button
                  type="button"
                  onClick={handleAddLineItem}
                  title="Simpan sebagai bungkusan sendiri, lalu isi ulang semua field (gudang/PIC/jatuh tempo/note) untuk barang berikutnya"
                  className="shrink-0 inline-flex items-center justify-center rounded-md border border-brand-500/50 text-brand-600 text-sm font-semibold px-3 py-2 hover:bg-brand-500/10"
                >
                  + Item
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Catatan</label>
              <input type="text" value={formNote} onChange={(e) => setFormNote(e.target.value)} className="glass-input" placeholder="Optional" />
            </div>

            {lineItems.length > 0 && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-muted mb-1.5">
                  Barang dalam pengajuan ini ({lineItems.length})
                </label>
                <div className="flex flex-wrap gap-2">
                  {lineItems.map((li) => (
                    <span
                      key={li.item_id}
                      className="inline-flex items-center gap-2 rounded-full border border-white/40 dark:border-white/15 bg-white/60 dark:bg-white/5 px-3 py-1 text-xs"
                      title={`PIC: ${li.pic_name} | Jatuh tempo: ${li.due_date}${li.note ? ` | ${li.note}` : ""}`}
                    >
                      {li.label} × {li.quantity} @ {li.location_name} — {li.pic_name} (jatuh tempo {li.due_date})
                      <button
                        type="button"
                        onClick={() => handleRemoveLineItem(li.item_id)}
                        className="text-red-500 hover:text-red-700 font-bold leading-none"
                        title="Hapus barang ini"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Nama PIC / Peminjam *</label>
              <input type="text" value={formPicName} onChange={(e) => setFormPicName(e.target.value)} className="glass-input" placeholder="e.g. Budi - HRD" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1.5">Tanggal Jatuh Tempo *</label>
              <input type="date" value={formDueDate} onChange={(e) => setFormDueDate(e.target.value)} className="glass-input" />
            </div>

            {formError && (
              <div className="md:col-span-2 text-sm rounded-xl px-4 py-3 border"
                style={{ color: "var(--danger)", background: "var(--danger-subtle)", borderColor: "color-mix(in srgb, var(--danger) 30%, transparent)" }}>
                {formError}
              </div>
            )}
            {formSuccess && (
              <div className="md:col-span-2 text-sm rounded-xl px-4 py-3 border"
                style={{ color: "var(--success)", background: "var(--success-subtle)", borderColor: "color-mix(in srgb, var(--success) 30%, transparent)" }}>
                {formSuccess}
              </div>
            )}
            <div className="md:col-span-2 flex justify-end">
              <button type="submit" disabled={formLoading} className="btn-primary">
                {formLoading ? "Mengirim..." : "Kirim Pengajuan"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-3 flex-wrap">
        <CustomSelect
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="Semua Status"
          className="w-48"
          options={[
            { value: "PENDING",   label: "Menunggu"              },
            { value: "ACTIVE",    label: "Disetujui"             },
            { value: "RETURNING", label: "Menunggu Konfirmasi"   },
            { value: "RETURNED",  label: "Dikembalikan"          },
            { value: "REJECTED",  label: "Ditolak"               },
          ]}
        />
      </div>

      {/* Tabel */}
      <div className="glass-table-wrap overflow-x-auto">
        <table className="glass-table">
          <thead>
            <tr>
              <th className="text-left">Referensi</th>
              <th className="text-left">Barang</th>
              <th className="text-left">Gudang</th>
              <th className="!text-right">Qty</th>
              <th className="text-left">PIC</th>
              <th className="text-left">Jatuh Tempo</th>
              <th className="text-left">Status</th>
              <th className="text-left">Keterangan</th>
              <th className="text-left">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="text-center py-8 text-muted">Loading...</td>
              </tr>
            ) : loans.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-8 text-muted">Belum ada pengajuan peminjaman.</td>
              </tr>
            ) : (
              loans.map((loan) => (
                <tr key={loan.id}>
                  <td className="font-mono text-xs">{loan.reference_code}</td>
                  <td>
                    <div className="font-medium text-strong">{loan.item_name}</div>
                    <div className="text-xs text-muted">{loan.item_code}</div>
                  </td>
                  <td className="text-sm">{loan.location_name}</td>
                  <td className="text-right font-mono">{loan.quantity} {loan.item_unit}</td>
                  <td className="text-sm">{loan.pic_name}</td>
                  <td className="text-sm font-mono">{loan.due_date}</td>
                  <td>{statusBadge(loan.status)}</td>
                  <td className="text-xs text-muted">
                    {loan.status === "REJECTED" && loan.rejected_reason
                      ? <span style={{ color: "var(--danger)" }}>{loan.rejected_reason}</span>
                      : loan.status === "RETURNED"
                        ? new Date(loan.returned_at).toLocaleDateString("id-ID")
                      : loan.status === "RETURNING"
                        ? <span className="text-xs" style={{ color: "var(--warning)" }}>Menunggu konfirmasi admin</span>
                        : "-"}
                  </td>
                  <td>
                    {loan.status === "ACTIVE" && (
                      <button
                        type="button"
                        onClick={() => {
                          setReturnReqModal(loan);
                          setReturnReqPhoto(null);
                          setReturnReqPhotoPreview(null);
                          setReturnReqNote("");
                          setReturnReqError("");
                        }}
                        className="btn-sm btn-outline whitespace-nowrap"
                      >
                        Ajukan Kembali
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Ajukan Pengembalian */}
      {returnReqModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="glass-panel rounded-2xl p-6 w-full max-w-md shadow-glass">
            <h3 className="text-base font-semibold text-strong mb-1">Ajukan Pengembalian</h3>
            <p className="text-sm text-muted mb-4">
              <strong>{returnReqModal.reference_code}</strong> — {returnReqModal.item_name} x{returnReqModal.quantity}
            </p>

            <div className="space-y-4">
              {/* Photo upload — wajib */}
              <div>
                <label className="block text-xs font-medium text-muted mb-1.5">
                  Foto Pengembalian <span className="text-red-400">*</span>
                </label>
                {returnReqPhotoPreview ? (
                  <div className="mb-2 flex items-start gap-3">
                    <img
                      src={returnReqPhotoPreview}
                      alt="Preview"
                      className="w-24 h-24 object-cover rounded-xl border border-white/20"
                    />
                    <button
                      type="button"
                      onClick={() => { setReturnReqPhoto(null); setReturnReqPhotoPreview(null); if (returnReqPhotoRef.current) returnReqPhotoRef.current.value = ""; }}
                      className="text-xs text-red-400 hover:text-red-300 mt-1"
                    >
                      Hapus foto
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2 flex-wrap">
                    <label className="btn-outline !px-3 !py-1.5 !text-xs cursor-pointer">
                      <input
                        ref={returnReqPhotoRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleReturnReqPhotoChange}
                      />
                      Upload Foto
                    </label>
                    <label className="btn-outline !px-3 !py-1.5 !text-xs cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleReturnReqPhotoChange}
                      />
                      Ambil dari Kamera
                    </label>
                  </div>
                )}
                <p className="mt-1 text-[11px] text-muted">Foto kondisi barang saat dikembalikan. Wajib diisi.</p>
              </div>

              {/* Optional note */}
              <div>
                <label className="block text-xs font-medium text-muted mb-1.5">Catatan (opsional)</label>
                <input
                  type="text"
                  value={returnReqNote}
                  onChange={(e) => setReturnReqNote(e.target.value)}
                  className="glass-input"
                  placeholder="Kondisi barang, keterangan tambahan..."
                />
              </div>
            </div>

            {returnReqError && (
              <p className="text-sm text-red-500 mt-3">{returnReqError}</p>
            )}

            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={() => setReturnReqModal(null)}
                disabled={returnReqLoading}
                className="btn-outline"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleSubmitReturnReq}
                disabled={returnReqLoading || !returnReqPhoto}
                className="btn-primary"
              >
                {returnReqLoading ? "Mengirim..." : "Kirim Pengembalian"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyLoanRequests;
